import React from "react";
import { Info, Calendar, Database } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface DataSourceInfoProps {
  dataType:
    | "deforestation"
    | "land-loss"
    | "desertification"
    | "species"
    | "weather"
    | "climate"
    | "satellite"
    | "extreme-weather"
    | "iceberg"
    | "carbon-analytics";
  updatedAt?: string;
  dbSources?: string[] | string;
}

const DataSourceInfo: React.FC<DataSourceInfoProps> = ({
  dataType,
  updatedAt,
  dbSources,
}) => {
  const getDataSourceInfo = (type: string) => {
    switch (type) {
      case "deforestation":
        return {
          sources: [
            "FAO Global Forest Resources Assessment (FRA) 2025",
            "WRI Global Forest Watch",
            "MODIS Terra/Aqua Satellite Imagery",
            "Landsat 8/9 & ESA Sentinel-2",
          ],
          methodology:
            "Forest cover change derived from FAO FRA national reporting combined with satellite-based canopy loss alerts from Global Forest Watch. Gross and net deforestation rates are reconciled against FAO FRA 2025 country submissions using standardised forest definitions (land >0.5 ha, canopy cover >10%, height >5 m).",
          resolution:
            "30-meter satellite analysis (Landsat/Sentinel-2) for spatially explicit change; national inventory aggregates from FAO FRA 2025 for verified area statistics.",
          updateFrequency:
            "Near-real-time satellite alerts (Global Forest Watch) refreshed weekly; national inventory data updated on the FAO FRA five-year cycle (latest: 2025).",
        };
      case "land-loss":
        return {
          sources: [
            "FAO Global Forest Resources Assessment (FRA) 2025",
            "WRI Global Forest Watch",
            "UNCCD Global Land Outlook",
            "Copernicus Global Land Service",
          ],
          methodology:
            "Integrated land-use change analysis combining FAO FRA national land-category reporting, satellite-derived cover change from Global Forest Watch, and UNCCD land degradation neutrality baselines. Net land loss is calculated as the difference between land converted from natural cover and land restored or reforested within the reporting period.",
          resolution:
            "Sub-national polygons at 30-meter satellite resolution, aggregated to national and regional administrative boundaries following FAO and UNCCD reporting units.",
          updateFrequency:
            "Annual progress reports from FAO and UNCCD; satellite-derived change layers updated quarterly via Copernicus Land Monitoring Service.",
        };
      case "desertification":
        return {
          sources: [
            "UNCCD Global Land Outlook",
            "FAO Global Forest Resources Assessment (FRA) 2025",
            "MODIS NDVI & LPDAAC Time Series",
            "ISRIC World Soil Information",
          ],
          methodology:
            "Land degradation and desertification assessment following the UNCCD Land Degradation Neutrality (LDN) framework, using three sub-indicators: land cover change (FAO FRA), land productivity dynamics (MODIS NDVI), and soil organic carbon (ISRIC). Desertification risk levels are derived from the UNCCD Global Land Outlook 2 (2022) country-level vulnerability index; GLO3 due August 2026.",
          resolution:
            "Sub-national assessment units aligned with UNCCD national reporting boundaries; MODIS NDVI trends at 250-meter resolution aggregated to regional polygons.",
          updateFrequency:
            "UNCCD Global Land Outlook published on a ~4-year cycle (latest: GLO2 2022; GLO3 expected August 2026); NDVI productivity indicators updated annually; soil carbon stocks reassessed with each FAO FRA cycle.",
        };
      case "species":
        return {
          sources: [
            "IUCN Red List of Threatened Species (v2025-2)",
            "Global Biodiversity Information Facility (GBIF)",
            "CITES Trade Database",
            "iNaturalist Community Observations",
          ],
          methodology:
            "Species conservation status assessed following the IUCN Red List Categories and Criteria (v3.1). Population trend analysis draws on the latest IUCN assessment cycle (v2025-2), cross-referenced with GBIF occurrence records for distribution verification and CITES appendix listings for trade-pressure context.",
          resolution:
            "Species-level assessments with range maps at 10 km × 10 km grid cells (IUCN standard); point-occurrence records from GBIF and iNaturalist at coordinate precision.",
          updateFrequency:
            "IUCN Red List updated multiple times per year (current version: v2025-2); GBIF occurrence data ingested in near-real-time; iNaturalist research-grade observations synced quarterly.",
        };
      case "weather":
        return {
          sources: [
            "OpenWeatherMap API",
            "Global network of weather stations",
            "Weather radar and satellite imagery",
            "Meteorological stations planetwide",
          ],
          methodology:
            "Real-time observations from meteorological stations, weather radars, and satellite imagery",
          resolution: "Point-based measurements with global coverage",
          updateFrequency: "Every 10 minutes from weather stations planetwide",
        };
      case "climate":
        return {
          sources: [
            "NASA POWER Database",
            "ECMWF ERA5 Reanalysis",
            "NOAA Climate Data",
            "Copernicus Climate Change Service",
          ],
          methodology:
            "Historical climate analysis using satellite-derived meteorological parameters and reanalysis data",
          resolution: "0.5° to 1° spatial resolution (approximately 50-100 km)",
          updateFrequency: "Monthly updates with annual comprehensive analysis",
        };
      case "satellite":
        return {
          sources: [
            "ESA Sentinel Satellites",
            "NASA Landsat Program",
            "MODIS Terra/Aqua",
            "Commercial Satellite Providers",
          ],
          methodology:
            "Multi-spectral and optical satellite imagery analysis for environmental monitoring",
          resolution: "10-30 meter spatial resolution for optical imagery",
          updateFrequency:
            "Daily to weekly depending on satellite constellation",
        };
      case "extreme-weather":
        return {
          sources: [
            "GDACS (Global Disaster Alert and Coordination System)",
            "YouTube Data API v3",
            "Social media monitoring platforms",
            "News and media coverage analysis",
          ],
          methodology:
            "Combined analysis of official disaster reports and social media sentiment to assess event impact and public awareness",
          resolution:
            "Regional to global event coverage with social media reach metrics",
          updateFrequency:
            "Real-time event monitoring with weekly trend analysis",
        };
      case "iceberg":
        return {
          sources: [
            "USNIC / Copernicus iceberg tracking",
            "IPCC AR6 WGI Chapter 9 (2021) cryosphere & melt-rate estimates",
            "Journal of Glaciology mass-loss literature (2022–2025)",
            "Supabase curated iceberg inventory",
          ],
          methodology:
            "Curated iceberg tracking and melt-rate estimates derived from agency-measured positions and peer-reviewed mass balance studies, normalized for fast globe rendering.",
          resolution:
            "Individual iceberg tracking points with estimated thickness, volume, retreat, and mass loss metrics.",
          updateFrequency:
            "Updated whenever new iceberg tracking or melt model inputs are posted to Supabase.",
        };
      case "carbon-analytics":
        return {
          sources: [
            "Global Carbon Atlas (Global Carbon Project)",
            "EDGAR Database (European Commission)",
            "Climate Action Tracker",
            "National Greenhouse Gas Inventories",
            "UNFCCC National Communications",
            "Carbon Monitor Real-time CO₂",
            "IEA World Energy Balances (sector percentages)",
            "IPCC AR6 WG3 (2022) — 1.5°C reduction pathway",
          ],
          methodology:
            "Regional and national CO₂ emissions analysis using fossil fuel consumption data, land use change estimates, and industrial activity statistics with annual time series processing. Sector breakdowns are estimated using IEA global average profiles and may not reflect country-specific economic structures.",
          resolution:
            "National and sub-national administrative boundaries with yearly temporal resolution. Sector estimates are modeled approximations.",
          updateFrequency:
            "Annual updates with quarterly estimates for recent years. Sector profiles based on global statistical averages.",
        };
      default:
        return {
          sources: ["Multiple satellite and ground-based sources"],
          methodology: "Multi-source data integration and analysis",
          resolution: "Variable spatial resolution",
          updateFrequency: "Regular updates",
        };
    }
  };

  const formatLastUpdated = (timestamp?: string) => {
    if (!timestamp) return "Unknown";
    try {
      const date = new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return "Unknown";
    }
  };

  const sourceInfo = getDataSourceInfo(dataType);

  const parsedDbSources: string[] | null = (() => {
    if (!dbSources) return null;
    if (Array.isArray(dbSources) && dbSources.length > 0) return dbSources;
    if (typeof dbSources === "string") {
      // Try JSON array first
      try {
        const parsed = JSON.parse(dbSources);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch {
        // not JSON
      }
      // Fall back to semicolon-delimited string
      const parts = dbSources
        .split(";")
        .map((s) => s.trim())
        .filter(Boolean);
      if (parts.length > 0) return parts;
    }
    return null;
  })();

  return (
    <div className="mt-8 p-4 bg-muted/30 rounded-lg border border-border/50 text-left">
      <div className="flex items-center gap-2 mb-5">
        <Info className="h-4 w-4 text-[#40e0d0]" />
        <h5 className="font-semibold text-foreground text-left">
          Data Sources & Information
        </h5>
      </div>

      <div className="space-y-3 text-sm text-left text-[.8rem]">
        {/* Data Sources */}
        <div className="text-left mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Database className="h-3 w-3 text-[#40e0d0]" />
            <span className="font-medium text-foreground text-left">
              Primary Data Sources:
            </span>
          </div>
          <ul className="list-disc list-inside space-y-1 ml-4 text-muted-foreground text-left">
            {(parsedDbSources ?? sourceInfo.sources).map((source, index) => (
              <li key={index} className="text-left">
                {source}
              </li>
            ))}
          </ul>
        </div>

        {/* Methodology */}
        <div className="text-left mt-2">
          <span className="font-medium text-foreground text-left">
            Methodology:{" "}
          </span>
          <span className="text-muted-foreground text-left">
            {sourceInfo.methodology}
          </span>
        </div>

        {/* Spatial Resolution */}
        <div className="text-left">
          <span className="font-medium text-foreground text-left">
            Spatial Resolution:{" "}
          </span>
          <span className="text-muted-foreground text-left">
            {sourceInfo.resolution}
          </span>
        </div>

        {/* Update Frequency */}
        <div className="text-left">
          <span className="font-medium text-foreground text-left">
            Update Frequency:{" "}
          </span>
          <span className="text-muted-foreground text-left">
            {sourceInfo.updateFrequency}
          </span>
        </div>

        {/* Last Updated */}
        <div className="flex items-center gap-2 p-2 border-t border-border/30 text-left bg-[#1bebd6]/10">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          <span className="font-medium text-foreground text-left">
            Last Updated:{" "}
          </span>
          <span className="text-muted-foreground text-left">
            {formatLastUpdated(updatedAt)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default DataSourceInfo;
